// Epoch 18: Enhanced Admin Dashboard for CSR Campaign Assignment
// Bulk Assignment Panel - Advanced bulk operations interface
// Date: July 26, 2025

import React, { useState } from 'react';
import { 
  validateBulkAssignment, 
  generateAssignmentSummary,
  CSRWorkload,
  AssignmentSummary
} from '../../utils/csrAssignment';

interface BulkAssignmentPanelProps {
  selectedCampaigns: string[];
  csrWorkloads: CSRWorkload[];
  onBulkAssign: (csrId: string | null) => Promise<void>;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onClose: () => void;
  isVisible: boolean;
  isProcessing?: boolean;
  workloadThreshold?: number;
}

const BulkAssignmentPanel: React.FC<BulkAssignmentPanelProps> = ({
  selectedCampaigns,
  csrWorkloads,
  onBulkAssign,
  onSelectAll,
  onClearSelection,
  onClose,
  isVisible,
  isProcessing = false,
  workloadThreshold = 150
}) => {
  const [selectedCSR, setSelectedCSR] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [assignmentSummary, setAssignmentSummary] = useState<AssignmentSummary | null>(null);
  const [validationError, setValidationError] = useState<string>('');

  if (!isVisible) return null;

  // Handle CSR selection change
  const handleCSRSelection = (csrId: string) => {
    setSelectedCSR(csrId);
    setValidationError(''); // Clear any validation errors
    
    if (csrId && selectedCampaigns.length > 0) {
      const summary = generateAssignmentSummary(selectedCampaigns, csrId, csrWorkloads, workloadThreshold);
      setAssignmentSummary(summary);
    } else {
      setAssignmentSummary(null);
    }
  };

  // Handle bulk assignment with validation
  const handleBulkAssignment = async () => {
    if (!selectedCSR || selectedCampaigns.length === 0) return;

    const validation = validateBulkAssignment(selectedCampaigns, selectedCSR, csrWorkloads, workloadThreshold);
    
    if (!validation.isValid) {
      setValidationError(validation.error || 'Assignment validation failed');
      return;
    }
    
    // Clear any previous validation errors
    setValidationError('');

    // Show confirmation for large assignments
    if (selectedCampaigns.length > 10) {
      setShowConfirmation(true);
      return;
    }

    await performBulkAssignment();
  };

  // Perform the actual bulk assignment
  const performBulkAssignment = async () => {
    try {
      await onBulkAssign(selectedCSR || null);
      
      // Reset state after successful assignment
      setSelectedCSR('');
      setAssignmentSummary(null);
      setShowConfirmation(false);
    } catch (error) {
      console.error('Bulk assignment failed:', error);
    }
  };

  // Handle unassign all selected
  const handleUnassignAll = async () => {
    if (selectedCampaigns.length === 0) return;
    
    try {
      await onBulkAssign(null);
      setSelectedCSR('');
      setAssignmentSummary(null);
    } catch (error) {
      console.error('Bulk unassignment failed:', error);
    }
  };

  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Panel Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bulk Assignment Operations</h3>
            <p className="text-sm text-gray-600">
              Assign multiple campaigns to CSRs efficiently
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          disabled={isProcessing}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Panel Content */}
      <div className="p-4 space-y-4">
        {/* Selection Management */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-4">
            <div className="text-sm font-medium text-blue-900">
              {selectedCampaigns.length} campaigns selected
            </div>
            <div className="flex space-x-2">
              <button
                onClick={onSelectAll}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                disabled={isProcessing}
              >
                Select All Visible
              </button>
              <button
                onClick={onClearSelection}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                disabled={isProcessing}
              >
                Clear Selection
              </button>
            </div>
          </div>
          
          {selectedCampaigns.length > 0 && (
            <button
              onClick={handleUnassignAll}
              className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              disabled={isProcessing}
            >
              Unassign All Selected
            </button>
          )}
        </div>

        {/* CSR Selection and Assignment */}
        {selectedCampaigns.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CSR Selection */}
            <div className="space-y-2">
              <label htmlFor="bulk-csr-select" className="block text-sm font-medium text-gray-700">
                Assign to CSR
              </label>
              <select
                id="bulk-csr-select"
                value={selectedCSR}
                onChange={(e) => handleCSRSelection(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isProcessing}
              >
                <option value="">Select CSR...</option>
                {csrWorkloads
                  .sort((a, b) => a.total_workload_score - b.total_workload_score)
                  .map((csr) => (
                    <option key={csr.csr_id} value={csr.csr_id}>
                      {csr.csr_email} (Workload: {csr.total_workload_score})
                    </option>
                  ))}
              </select>
            </div>

            {/* Assignment Action */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Action
              </label>
              <button
                onClick={handleBulkAssignment}
                disabled={!selectedCSR || selectedCampaigns.length === 0 || isProcessing}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  `Assign ${selectedCampaigns.length} Campaign${selectedCampaigns.length !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Validation Error Display */}
        {validationError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-red-800">Assignment Blocked</h4>
                <p className="text-sm text-red-700 mt-1">{validationError}</p>
                <p className="text-xs text-red-600 mt-2">
                  You can adjust the workload threshold using the ⚙️ Threshold button in the header.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Summary */}
        {assignmentSummary && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">Assignment Impact</h4>
            <div className="grid grid-cols-2 gap-4 text-xs text-yellow-700">
              <div>
                <div className="font-medium">Target CSR:</div>
                <div>{assignmentSummary.targetCSREmail}</div>
              </div>
              <div>
                <div className="font-medium">Current Workload:</div>
                <div>{assignmentSummary.currentWorkload} points</div>
              </div>
              <div>
                <div className="font-medium">Workload Increase:</div>
                <div>+{assignmentSummary.workloadIncrease} points</div>
              </div>
              <div>
                <div className="font-medium">Projected Workload:</div>
                <div className={assignmentSummary.projectedWorkload > 20 ? 'font-bold text-yellow-900' : ''}>
                  {assignmentSummary.projectedWorkload} points
                </div>
              </div>
            </div>
            {assignmentSummary.projectedWorkload > 20 && (
              <div className="mt-2 text-xs text-yellow-800">
                ⚠️ This assignment will result in high workload for the selected CSR.
              </div>
            )}
          </div>
        )}

        {/* Quick Assignment Suggestions */}
        {selectedCampaigns.length > 0 && csrWorkloads.length > 0 && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="text-sm font-medium text-green-800 mb-2">Quick Assignment Suggestions</h4>
            <div className="flex flex-wrap gap-2">
              {csrWorkloads
                .sort((a, b) => a.total_workload_score - b.total_workload_score)
                .slice(0, 3)
                .map((csr) => (
                  <button
                    key={csr.csr_id}
                    onClick={() => handleCSRSelection(csr.csr_id)}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                    disabled={isProcessing}
                  >
                    {csr.csr_email} ({csr.total_workload_score})
                  </button>
                ))}
            </div>
            <div className="text-xs text-green-600 mt-1">
              Showing CSRs with lowest current workload
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && assignmentSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Bulk Assignment
              </h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  You are about to assign <strong>{assignmentSummary.totalCampaigns} campaigns</strong> to{' '}
                  <strong>{assignmentSummary.targetCSREmail}</strong>.
                </p>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Current Workload: {assignmentSummary.currentWorkload}</div>
                    <div>Workload Increase: +{assignmentSummary.workloadIncrease}</div>
                    <div className="col-span-2 font-medium">
                      New Total: {assignmentSummary.projectedWorkload} points
                    </div>
                  </div>
                </div>
                <p>This action cannot be undone. Continue?</p>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  onClick={performBulkAssignment}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Confirm Assignment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkAssignmentPanel;